import React, { useState, useEffect } from "react";
import { ExistingGLAccount } from "../models/gl-account";
import BaseButtonPrimary from "./BaseButtonPrimary";
import BaseButtonSecondary from "./BaseButtonSecondary";
import currencyFormatter from "../currency";
import { DateTime } from "luxon";
import BaseInputDate from "./BaseInputDate";
import BaseInputSelect from "./BaseInputSelect";
import BaseInputTextArea from "./BaseInputTextArea";
import BaseInputNumber from "./BaseInputNumber";
import ImportQBOAccountsDialog from "./ImportQBOAccountsDialog";
import { QBOAccountResponse } from "../database";
import { GLAccount_CreateAPI, GLAccountManager } from "../models/gl-account";
import { User } from "firebase/auth";
import { DbRead, DbWrite } from "../database";
import BaseModal from "./BaseModal";

interface BankDepositConfirmationDialogProps {
  isOpen: boolean;
  quickbooksEnabled: boolean;
  onClose: () => void;
  onConfirm: (
    glAccount: ExistingGLAccount,
    depositDate: string,
    memo: string,
    depositAmount: number,
  ) => void;
  totalAmount: number;
  paymentCount: number;
  depositType: "cash" | "check" | "ach" | "credit-card" | "all";
  isLoading: boolean;
  glAccounts: ExistingGLAccount[];
  siteKey: string;
  firebaseUser: User;
  currency: string;
  onImportAccounts?: (accounts: GLAccount_CreateAPI[]) => Promise<void>;
}

export default function BankDepositConfirmationDialog({
  isOpen,
  quickbooksEnabled,
  onClose,
  onConfirm,
  totalAmount,
  paymentCount,
  depositType,
  isLoading,
  glAccounts,
  siteKey,
  firebaseUser,
  currency,
  onImportAccounts,
}: BankDepositConfirmationDialogProps): JSX.Element {
  const [selectedGLAccountId, setSelectedGLAccountId] = useState<string>("");
  const [depositDate, setDepositDate] = useState<string>(
    DateTime.now().toISO(),
  );
  const [memo, setMemo] = useState<string>("");
  const [depositAmount, setDepositAmount] = useState<number>(totalAmount);

  // State for import accounts dialog
  const [importQBODialogOpen, setImportQBODialogOpen] =
    useState<boolean>(false);
  const [qboChartOfAccounts, setQboChartOfAccounts] = useState<
    QBOAccountResponse[] | null
  >(null);
  const [isLoadingQBOAccounts, setIsLoadingQBOAccounts] =
    useState<boolean>(false);

  // Format the deposit type for display and memo
  const formatDepositType = (type: string): string => {
    switch (type) {
      case "cash":
        return "Cash";
      case "check":
        return "Check";
      case "ach":
        return "ACH/EFT";
      case "credit-card":
        return "Credit Card";
      case "all":
        return "Mixed Payment";
      default:
        return type;
    }
  };

  // Reset selected account and set default memo when dialog opens or deposit type changes
  useEffect(() => {
    if (isOpen) {
      setSelectedGLAccountId("");
      setDepositDate(DateTime.now().toISO());
      setDepositAmount(totalAmount);
      const formattedType = formatDepositType(depositType);
      setMemo(`${formattedType} Deposit`);
    }
  }, [isOpen, depositType, glAccounts, totalAmount]);

  const handleConfirm = () => {
    const selectedAccount = glAccounts.find(
      (account) => account.id === selectedGLAccountId,
    );
    if (selectedAccount) {
      onConfirm(selectedAccount, depositDate, memo, depositAmount);
    }
  };

  const handleDateChange = (date: Date) => {
    setDepositDate(DateTime.fromJSDate(date).toISO());
  };

  const getDepositTitle = (): string => {
    return `Confirm ${formatDepositType(depositType)} Deposit`;
  };

  const handleOpenImportQBOAccountsDialog = async () => {
    try {
      setIsLoadingQBOAccounts(true);
      const accounts = await DbRead.gLAccounts.getQBOAccounts(siteKey);
      setQboChartOfAccounts(accounts);
      setImportQBODialogOpen(true);
    } catch (error) {
      console.error("Error fetching QuickBooks accounts:", error);
    } finally {
      setIsLoadingQBOAccounts(false);
    }
  };

  const handleImportSelectedGLAccounts = async (
    accountsToImport: GLAccount_CreateAPI[],
  ) => {
    try {
      // Keep track of successful and failed imports
      const results = {
        success: 0,
        failed: 0,
      };

      // Import each account one by one
      for (const account of accountsToImport) {
        try {
          // Ensure the account has the correct siteKey, currency, and user info
          const newGLAccount: GLAccount_CreateAPI = {
            ...account,
            currency: currency,
            deleted: false,
            lastModifiedBy: firebaseUser.uid,
            siteKey: siteKey,
          };

          const validatedAccount = GLAccountManager.parseCreate(newGLAccount);
          await DbWrite.gLAccounts.create(validatedAccount);
          results.success++;
        } catch (error) {
          console.error(`Error importing account ${account.name}:`, error);
          results.failed++;
        }
      }

      // Show success message
      if (results.success > 0) {
        // Use the onImportAccounts callback if provided, otherwise show a basic success message
        if (onImportAccounts) {
          await onImportAccounts(accountsToImport);
        }
        console.log(
          `Successfully imported ${results.success} account${
            results.success !== 1 ? "s" : ""
          }${results.failed > 0 ? ` (${results.failed} failed)` : ""}`,
        );
      } else if (results.failed > 0) {
        console.error(
          `Failed to import ${results.failed} account${
            results.failed !== 1 ? "s" : ""
          }`,
        );
      }
    } catch (error) {
      console.error("Error importing accounts:", error);
    } finally {
      setImportQBODialogOpen(false);
    }
  };

  const isAmountGreaterThanPayments = depositAmount > totalAmount;
  const isValidAmount = depositAmount >= totalAmount && depositAmount > 0;

  return (
    <BaseModal
      open={isOpen}
      closeModal={onClose}
      title={getDepositTitle()}
      parentDivStyles="p-6 max-w-lg text-left"
    >
      <div className="space-y-4">
        <div className="rounded-md bg-gray-50 p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="text-sm font-medium text-gray-500">
              Payment Total:
            </div>
            <div className="text-sm font-semibold text-gray-900">
              {currencyFormatter(totalAmount, "USD")}
            </div>

            <div className="text-sm font-medium text-gray-500">Payments:</div>
            <div className="text-sm font-semibold text-gray-900">
              {paymentCount}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="">
            <BaseInputSelect
              admin={true}
              value={selectedGLAccountId}
              onChange={(e) => setSelectedGLAccountId(e.target.value)}
              inputName="glAccountSelect"
              text="Select Account"
              required={true}
            >
              <option value="" disabled>
                Select an Account
              </option>
              {glAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </BaseInputSelect>
            {glAccounts.length === 0 && (
              <p className="text-sm text-red-500">
                No GL accounts available. Please create a GL account first.
              </p>
            )}
            {quickbooksEnabled && (
              <button
                type="button"
                onClick={handleOpenImportQBOAccountsDialog}
                disabled={isLoadingQBOAccounts}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline disabled:text-gray-400"
              >
                {isLoadingQBOAccounts
                  ? "Loading..."
                  : "Import more accounts from QuickBooks..."}
              </button>
            )}
          </div>

          <div className="">
            <BaseInputNumber
              admin={true}
              value={depositAmount === 0 ? "" : depositAmount}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === "") {
                  setDepositAmount(0);
                } else {
                  const value = e.target.valueAsNumber;
                  setDepositAmount(isNaN(value) ? 0 : value);
                }
              }}
              inputName="depositAmount"
              text="Deposit Amount"
              required={true}
              step="0.01"
              min="0"
            />
            {isAmountGreaterThanPayments && (
              <p className="mt-2 rounded-md bg-blue-100 p-2 text-sm text-blue-800">
                Note: This amount is greater than the sum of all linked
                payments, which may indicate that you have not selected all
                payments, or that other funds are included in this deposit. You
                can still create the deposit, but please review the payments and
                amount to ensure accuracy.
              </p>
            )}
            {!isValidAmount && depositAmount > 0 && (
              <p className="mt-1 text-sm text-red-600">
                Deposit amount must be greater than or equal to the payment
                total.
              </p>
            )}
          </div>

          <div className="pt-6">
            <BaseInputDate
              value={depositDate}
              onChange={handleDateChange}
              inputName="depositDate"
              displayText="Deposit Date"
              required={true}
            />
          </div>
          <div className="pt-2">
            <BaseInputTextArea
              admin={true}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              inputName="depositMemo"
              text="Memo (Optional)"
              rows={3}
            />
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm text-gray-500">
            {`This will create a bank deposit in Stilt linked to these ${paymentCount} payments`}
          </p>
          {quickbooksEnabled && (
            <div className="flex flex-row items-center gap-2 rounded-md bg-gray-100 p-2">
              <img
                src="/quickbooks-2.svg"
                alt="QuickBooks"
                style={{ height: "24px" }}
              />
              <p className="text-sm text-gray-500">
                {`The Bank Deposit will be pushed to QuickBooks, clearing these payments from the undeposited funds account.`}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <BaseButtonSecondary onClick={onClose} disabled={isLoading}>
            Cancel
          </BaseButtonSecondary>
          <BaseButtonPrimary
            onClick={handleConfirm}
            disabled={
              !selectedGLAccountId ||
              isLoading ||
              glAccounts.length === 0 ||
              !isValidAmount
            }
            isBusy={isLoading}
          >
            {isLoading ? "Creating..." : "Create Deposit"}
          </BaseButtonPrimary>
        </div>
      </div>

      {quickbooksEnabled && (
        <ImportQBOAccountsDialog
          isDialogOpen={importQBODialogOpen}
          closeDialog={() => setImportQBODialogOpen(false)}
          siteKey={siteKey}
          firebaseUser={firebaseUser}
          currency={currency}
          onImportAccounts={handleImportSelectedGLAccounts}
          qboAccounts={qboChartOfAccounts}
          existingGLAccounts={glAccounts}
          isLoading={isLoadingQBOAccounts}
        />
      )}
    </BaseModal>
  );
}
